import { useState, useEffect, useRef, useCallback } from 'react';

export const useCamera = (options = {}) => {
  const [stream, setStream] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  // Get available camera devices
  const getDevices = useCallback(async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = deviceList
        .filter(device => device.kind === 'videoinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
          kind: device.kind,
        }));
      
      setDevices(videoDevices);
      
      // Set default device if none selected
      if (!selectedDeviceId && videoDevices.length > 0) {
        setSelectedDeviceId(videoDevices[0].deviceId);
      }
    } catch (err) {
      setError('Failed to get camera devices');
      console.error('Error getting devices:', err);
    }
  }, [selectedDeviceId]);

  // Start camera stream
  const startCamera = useCallback(async (deviceId) => {
    setIsLoading(true);
    setError(null);

    try {
      // Stop existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: options.video !== false ? {
          deviceId: deviceId || selectedDeviceId || undefined,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 }
        } : false,
        audio: options.audio || false
      };

      const newStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(newStream);

      // Attach stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }

      if (deviceId) {
        setSelectedDeviceId(deviceId);
      }
    } catch (err) {
      setError('Failed to start camera');
      console.error('Error starting camera:', err);
    } finally {
      setIsLoading(false);
    }
  }, [stream, selectedDeviceId, options.video, options.audio]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [stream]);

  // Switch camera device
  const switchCamera = useCallback(async (deviceId) => {
    await startCamera(deviceId);
  }, [startCamera]);

  // Initialize on mount
  useEffect(() => {
    getDevices();
  }, [getDevices]);

  // Auto-start camera when device is selected
  useEffect(() => {
    if (selectedDeviceId && !stream) {
      startCamera();
    }
  }, [selectedDeviceId, stream, startCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return {
    stream,
    devices,
    selectedDeviceId,
    isLoading,
    error,
    videoRef,
    startCamera,
    stopCamera,
    switchCamera,
    getDevices
  };
};